import { useParams } from "react-router-dom";
import { create } from "zustand";

import type { CommentID, ModelCommentV2 } from "../hooks/useComments";
import { useWebExport } from "../hooks/useWebExport";
import type TrackingAdapter from "../lib/TrackingAdapter";
import useTracker from "../utils/useTracker";

type ToolChangeSource = "tool_menu" | "keyboard_shortcut" | "mouse_click" | null;
type InternalTelemetryOptions = {
  tracker: InstanceType<typeof TrackingAdapter>;
  viewLink: string;
  webExportId: string | undefined;
};

const defaultNewCommentPosition: ModelCommentV2 = {
  commentPosition: { x: 0, y: 0, z: 0 },
  commentNormal: { x: 0, y: 0, z: 0 },
  cameraTarget: { x: 0, y: 0, z: 0 },
  cameraOrbit: { phi: 0, theta: 0, radius: 0 },
  fieldOfView: 0,
};

interface CommentsState {
  isInAddCommentMode: boolean;
  openCommentId: string | undefined;
  isNewCommentOpen: boolean;
  newCommentPosition: ModelCommentV2;
}

interface CommentsActions {
  setIsInAddCommentMode: (
    isInCommentMode: boolean,
    options: { source: ToolChangeSource } & InternalTelemetryOptions
  ) => void;
  toggleIsInAddCommentMode: (
    options: { source: ToolChangeSource } & InternalTelemetryOptions
  ) => void;
  setOpenCommentId: (commentId: CommentID | undefined) => void;
  setIsNewCommentOpen: (isNewCommentOpen: boolean) => void;
  newCommentPosition: ModelCommentV2;
  setNewCommentPosition: (newCommentPosition: ModelCommentV2) => void;
  reset: () => void;
}

const initialState: CommentsState = {
  isInAddCommentMode: false,
  openCommentId: undefined,
  isNewCommentOpen: false,
  newCommentPosition: defaultNewCommentPosition,
};

const useCommentsStoreInternal = create<CommentsState & CommentsActions>((set) => {
  return {
    ...initialState,
    setIsInAddCommentMode: (isInAddCommentMode, { source, tracker, viewLink, webExportId }) => {
      if (source) {
        tracker.trackActiveToolChanged({
          initiatedFrom: source,
          newTool: isInAddCommentMode ? "commenting" : "camera_control",
          viewLinkId: viewLink,
          webExportId,
        });
      }
      set({ isInAddCommentMode });
    },
    toggleIsInAddCommentMode: ({ source, tracker, viewLink, webExportId }) => {
      set((prev) => {
        const newVal = !prev.isInAddCommentMode;
        if (source) {
          tracker.trackActiveToolChanged({
            initiatedFrom: source,
            newTool: newVal ? "commenting" : "camera_control",
            viewLinkId: viewLink,
            webExportId,
          });
        }
        return { isInAddCommentMode: newVal };
      });
    },
    setOpenCommentId: (openCommentId: CommentID | undefined) => set({ openCommentId }),
    setIsNewCommentOpen: (isNewCommentOpen: boolean) => set({ isNewCommentOpen }),
    setNewCommentPosition: (newCommentPosition: ModelCommentV2) => set({ newCommentPosition }),
    reset: () => set(initialState),
  };
});

export const useCommentsStore = () => {
  const tracker = useTracker();
  const { viewLink = "" } = useParams();
  const { data: webExportData } = useWebExport();
  const commentStore = useCommentsStoreInternal();

  const setIsInAddCommentMode = (isInCommentMode: boolean, options: { source: ToolChangeSource }) =>
    commentStore.setIsInAddCommentMode(isInCommentMode, {
      ...options,
      tracker,
      viewLink,
      webExportId: webExportData?.webExport.webExportId,
    });
  const toggleIsInAddCommentMode = (options: { source: ToolChangeSource }) =>
    commentStore.toggleIsInAddCommentMode({
      ...options,
      tracker,
      viewLink,
      webExportId: webExportData?.webExport.webExportId,
    });

  return { ...commentStore, setIsInAddCommentMode, toggleIsInAddCommentMode };
};
